import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { ClientPaginatedTable } from 'components/table';
import * as TableConfig from './tablesConfig';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { useEffect, useState } from 'react';
import { NewAccountStatus } from 'constants/new_accounts.constants';
import { getCustomerWithTranslatedType } from './helper';
import { deleteNewAccountAndCompany, getNewAccounts } from 'services/new_accounts.service';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { useConfirmationDialog } from 'hooks/shared/useConfirmationDialog';
import { ConfirmationDialog } from 'components/ConfirmationDialog';

type IProps = {
	declinedAccounts: Record<string, any>;
};

export const DeclinedAccountsTable: React.FC<IProps> = ({ declinedAccounts }: IProps) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const [accounts, setAccounts] = useState<any>([]);
	const deleteAccountPermission = hasPermission([permissionEnums.REMOVAL_ACCOUNT]);

	const { dialogConfig, openDialog, handleConfirm, handleCancel } = useConfirmationDialog();

	useEffect(() => {
		if (declinedAccounts) {
			const data = getCustomerWithTranslatedType(declinedAccounts, t);
			setAccounts(data);
		}
	}, [declinedAccounts]);

	const handleDeleteClick = (accountId: number) => {
		const account = accounts.find((acc: any) => acc.id === accountId);
		if (!account) return;

		openDialog({
			title: t('page.newAccount.delete.confirmation.title'),
			message: t('page.newAccount.delete.confirmation.message').replace(
				'{accountName}',
				account.accountNameHebrew || account.accountNameEnglish
			),
			confirmLabel: t('actions.deleteAccount'),
			cancelLabel: t('actions.cancel'),
			onConfirm: async () => {
				try {
					await dispatch(deleteNewAccountAndCompany(accountId));
				} catch (error) {
					console.error('Failed to delete account:', error);
				}
			},
		});
	};

	const tableContent = (
		<Stack spacing={2}>
			{declinedAccounts?.length > 0 && (
				<>
					<Typography variant="subtitle" color={theme.palette.root.grey}>
						{declinedAccounts?.length + ' ' + t('messages.results')}
					</Typography>
					<ClientPaginatedTable
						rowsClass="collapse"
						data={TableConfig.tableData(accounts)}
						columns={TableConfig.buildColumns(
							NewAccountStatus.DENIED,
							t,
							deleteAccountPermission ? handleDeleteClick : undefined
						)}
					/>
				</>
			)}
		</Stack>
	);

	return (
		<>
			{tableContent}

			<ConfirmationDialog
				open={dialogConfig.open}
				title={dialogConfig.title}
				message={dialogConfig.message}
				confirmLabel={dialogConfig.confirmLabel}
				cancelLabel={dialogConfig.cancelLabel}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
				confirmColor="error"
				loading={dialogConfig.loading}
				error={dialogConfig.error}
			/>
		</>
	);
};
